import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  redirect,
} from '@tanstack/react-router';
import { LandingPage } from '@/pages/Landing';
import { LoginPage } from '@/pages/Login';
import { RegisterPage } from '@/pages/Register';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { ResetPasswordPage } from '@/pages/ResetPassword';
import { VerifyEmailPage } from '@/pages/VerifyEmail';
import { DashboardPage } from '@/pages/Dashboard';
import { SitesPage } from '@/pages/app/Sites';
import { AddSitePage } from '@/pages/app/AddSite';
import { TeamPage } from '@/pages/app/Team';
import { ContentPage } from '@/pages/app/Content';
import { ContentNewPage } from '@/pages/app/ContentNew';
import { ContentDetailPage } from '@/pages/app/ContentDetail';
import { PreferencesPage } from '@/pages/app/account/Preferences';
import { OrdersPage } from '@/pages/app/account/Orders';
import { ByokKeysPage } from '@/pages/app/account/ByokKeys';
import { BulkPage } from '@/pages/app/Bulk';
import { SchedulePage } from '@/pages/app/Schedule';
import { PackagesPage } from '@/pages/app/Packages';
import { CartCheckoutPage } from '@/pages/app/CartCheckout';
import { PricingPage } from '@/pages/public/Pricing';
import { ApiDocsPage } from '@/pages/public/ApiDocs';
import { PrivacyPolicyPage } from '@/pages/public/Privacy';
import { AUPPage } from '@/pages/public/AUP';
import { TermsPage } from '@/pages/public/Terms';
import { FAQPage } from '@/pages/public/FAQ';
import { ApiKeysPage } from '@/pages/app/account/ApiKeys';
import { InviteAcceptPage } from '@/pages/InviteAccept';
import { ThemeSettingsPage } from '@/pages/platform/ThemeSettings';
import { OAuthSettingsPage } from '@/pages/platform/OAuthSettings';
import { AIProvidersPage } from '@/pages/platform/AIProviders';
import { GeneralSettingsPage } from '@/pages/platform/GeneralSettings';
import { PlatformUsersPage } from '@/pages/platform/Users';
import { PlatformOrgsPage } from '@/pages/platform/Orgs';
import { PlatformDashboardPage } from '@/pages/platform/Dashboard';
import { BillingSettingsPage } from '@/pages/platform/BillingSettings';
import { MailerSettingsPage } from '@/pages/platform/MailerSettings';
import { PlatformInboxPage } from '@/pages/platform/Inbox';
import { PlatformInboxDetailPage } from '@/pages/platform/InboxDetail';
import { PlatformNotificationsPage } from '@/pages/platform/Notifications';
import { PlatformPackagesPage } from '@/pages/platform/Packages';
import { PromptTemplatesPage } from '@/pages/platform/PromptTemplates';
import { CryptoWalletsPage } from '@/pages/platform/CryptoWallets';
import { PlatformCartsPage } from '@/pages/platform/Carts';
import { PlatformExplorersPage } from '@/pages/platform/Explorers';
import { PlatformAnnouncementsPage } from '@/pages/platform/Announcements';
import { PlatformOrdersPage as PlatformAllOrdersPage } from '@/pages/platform/AllOrders';
import { AppShell } from '@/components/layout/AppShell';
import { PlatformLayout } from '@/components/layout/PlatformLayout';
import { useMe } from '@/hooks/useAuth';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$token',
  component: ResetPasswordPage,
});
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email/$token',
  component: VerifyEmailPage,
});
const publicPricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});
const publicApiDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api-docs',
  component: ApiDocsPage,
});
const publicPrivacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPolicyPage,
});
const publicAUPRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/aup',
  component: AUPPage,
});
const publicTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
});
const publicFaqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/faq',
  component: FAQPage,
});

// Authenticated app shell
function AppShellRoute() {
  const me = useMe();
  if (me.isPending) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Yükleniyor…</div>;
  }
  if (me.isError || !me.data) {
    throw redirect({ to: '/login' });
  }
  return (
    <AppShell user={me.data.user} orgs={me.data.organizations} activeOrgId={me.data.active_org_id}>
      <Outlet />
    </AppShell>
  );
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppShellRoute,
});

function DashboardInner() {
  const me = useMe();
  if (!me.data) return null;
  const activeOrg = me.data.organizations.find((o) => o.id === me.data!.active_org_id)!;
  return <DashboardPage user={me.data.user} activeOrg={activeOrg} />;
}

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: DashboardInner,
});

function PlaceholderPage({ label }: { label: string }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold">{label}</h1>
      <p className="text-muted-foreground mt-2">Bu bölüm yakında eklenecek.</p>
    </div>
  );
}

const sitesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sites',
  component: SitesPage,
});
const sitesNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sites/new',
  component: AddSitePage,
});
const contentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/content',
  component: ContentPage,
});
const contentNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/content/new',
  component: ContentNewPage,
});
const contentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/content/$id',
  component: ContentDetailPage,
});
const teamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/team',
  component: TeamPage,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InviteAcceptPage,
});
const packagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/packages',
  component: PackagesPage,
});
const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account/orders',
  component: OrdersPage,
});
const cartCheckoutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/checkout/cart/$cartId',
  component: CartCheckoutPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: () => <PlaceholderPage label="Ayarlar" />,
});
const preferencesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account/preferences',
  component: PreferencesPage,
});
const byokRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account/byok-keys',
  component: ByokKeysPage,
});
const apiKeysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account/api-keys',
  component: ApiKeysPage,
});
const bulkRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/content/bulk',
  component: BulkPage,
});
const scheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/schedule',
  component: SchedulePage,
});

// Platform (owner-only)
function PlatformShellRoute() {
  const me = useMe();
  if (me.isPending) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Yükleniyor…</div>;
  }
  if (me.isError || !me.data) {
    throw redirect({ to: '/login' });
  }
  if (me.data.user.platform_role !== 'owner') {
    throw redirect({ to: '/dashboard' });
  }
  return (
    <PlatformLayout user={me.data.user} orgs={me.data.organizations} activeOrgId={me.data.active_org_id}>
      <Outlet />
    </PlatformLayout>
  );
}

const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'platform',
  component: PlatformShellRoute,
});

const platformIndexRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform',
  component: PlatformDashboardPage,
});

const platformThemeRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/theme',
  component: ThemeSettingsPage,
});

const platformGeneralRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/general',
  component: GeneralSettingsPage,
});

const platformOauthRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/oauth',
  component: OAuthSettingsPage,
});

const platformAIProvidersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/ai-providers',
  component: AIProvidersPage,
});

const platformUsersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/users',
  component: PlatformUsersPage,
});

const platformOrgsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/orgs',
  component: PlatformOrgsPage,
});

const platformWebhooksRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/webhooks',
  component: BillingSettingsPage,
});
const platformMailerRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/mailer',
  component: MailerSettingsPage,
});
const platformInboxRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/inbox',
  component: PlatformInboxPage,
});
const platformInboxDetailRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/inbox/$id',
  component: PlatformInboxDetailPage,
});
const platformNotificationsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/notifications',
  component: PlatformNotificationsPage,
});
const platformPackagesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/packages',
  component: PlatformPackagesPage,
});
const platformPromptsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/prompt-templates',
  component: PromptTemplatesPage,
});
const platformCryptoWalletsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/crypto-wallets',
  component: CryptoWalletsPage,
});
const platformAllOrdersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/orders',
  component: PlatformAllOrdersPage,
});
const platformCartsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/carts',
  component: PlatformCartsPage,
});
const platformExplorersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/explorers',
  component: PlatformExplorersPage,
});
const platformAnnouncementsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/announcements',
  component: PlatformAnnouncementsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  publicPricingRoute,
  publicApiDocsRoute,
  publicPrivacyRoute,
  publicAUPRoute,
  publicTermsRoute,
  publicFaqRoute,
  inviteRoute,
  appRoute.addChildren([
    dashboardRoute,
    sitesRoute,
    sitesNewRoute,
    contentRoute,
    contentNewRoute,
    contentDetailRoute,
    teamRoute,
    packagesRoute,
    ordersRoute,
    cartCheckoutRoute,
    bulkRoute,
    scheduleRoute,
    settingsRoute,
    preferencesRoute,
    byokRoute,
    apiKeysRoute,
  ]),
  platformRoute.addChildren([
    platformIndexRoute,
    platformThemeRoute,
    platformGeneralRoute,
    platformOauthRoute,
    platformAIProvidersRoute,
    platformUsersRoute,
    platformOrgsRoute,
    platformWebhooksRoute,
    platformMailerRoute,
    platformInboxRoute,
    platformInboxDetailRoute,
    platformNotificationsRoute,
    platformPackagesRoute,
    platformPromptsRoute,
    platformCryptoWalletsRoute,
    platformAllOrdersRoute,
    platformCartsRoute,
    platformExplorersRoute,
    platformAnnouncementsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return <RouterProvider router={router} />;
}
